import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface AvailabilityWindow {
  weekday: number // 0=Sunday..6=Saturday
  startTime: string // 'HH:MM'
  endTime: string
}

export interface AvailabilityException {
  id: string
  date: string // 'YYYY-MM-DD'
  isAvailable: boolean
  startTime: string | null
  endTime: string | null
}

interface RpcResult {
  ok: boolean
  code?: string
}

/**
 * An instructor's own recurring weekly availability + one-off exceptions + cancellation
 * cutoff (migration 093). Windows are saved as a full-week replace via
 * replace_instructor_availability() (atomic delete+reinsert) -- never incremental
 * add/remove RPCs, matching the "edit the week, hit Save" UI shape. Exceptions are plain
 * single-row CRUD against instructor_availability_exceptions' own owner-scoped RLS.
 */
export function useInstructorAvailability() {
  const { user } = useAuth()
  const [windows, setWindows] = useState<AvailabilityWindow[]>([])
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([])
  const [cutoffHours, setCutoffHoursState] = useState<number>(24)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!user) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const [windowsRes, exceptionsRes, profileRes] = await Promise.all([
        supabase.from('instructor_availability_windows')
          .select('weekday, start_time, end_time')
          .eq('instructor_id', user.id)
          .order('weekday', { ascending: true })
          .order('start_time', { ascending: true }),
        supabase.from('instructor_availability_exceptions')
          .select('id, date, is_available, start_time, end_time')
          .eq('instructor_id', user.id)
          .order('date', { ascending: true }),
        supabase.from('instructor_profiles')
          .select('cancellation_cutoff_hours')
          .eq('user_id', user.id)
          .maybeSingle(),
      ])

      if (windowsRes.error || exceptionsRes.error) { setError('Could not load availability.'); return }

      setWindows((windowsRes.data ?? []).map(w => ({
        weekday: w.weekday, startTime: w.start_time.slice(0, 5), endTime: w.end_time.slice(0, 5),
      })))
      setExceptions((exceptionsRes.data ?? []).map(e => ({
        id: e.id, date: e.date, isAvailable: e.is_available,
        startTime: e.start_time ? e.start_time.slice(0, 5) : null,
        endTime: e.end_time ? e.end_time.slice(0, 5) : null,
      })))
      setCutoffHoursState(profileRes.data?.cancellation_cutoff_hours ?? 24)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchAll() }, [fetchAll])

  const saveWindows = useCallback(async (next: AvailabilityWindow[]): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('replace_instructor_availability', {
        p_windows: next.map(w => ({ weekday: w.weekday, start_time: w.startTime, end_time: w.endTime })),
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as RpcResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const addException = useCallback(async (fields: {
    date: string; isAvailable: boolean; startTime?: string | null; endTime?: string | null
  }): Promise<RpcResult> => {
    if (!user || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: insertErr } = await supabase.from('instructor_availability_exceptions').insert({
        instructor_id: user.id,
        date: fields.date,
        is_available: fields.isAvailable,
        start_time: fields.isAvailable ? fields.startTime : null,
        end_time: fields.isAvailable ? fields.endTime : null,
      })
      if (insertErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [user, saving, fetchAll])

  const removeException = useCallback(async (exceptionId: string): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: deleteErr } = await supabase.from('instructor_availability_exceptions')
        .delete().eq('id', exceptionId)
      if (deleteErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const setCutoffHours = useCallback(async (hours: number): Promise<RpcResult> => {
    if (!user || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: updateErr } = await supabase.from('instructor_profiles')
        .update({ cancellation_cutoff_hours: hours })
        .eq('user_id', user.id)
      if (updateErr) return { ok: false, code: 'internal_error' }
      setCutoffHoursState(hours)
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [user, saving])

  return {
    windows, exceptions, cutoffHours, loading, error, saving,
    saveWindows, addException, removeException, setCutoffHours, refresh: fetchAll,
  }
}
